import { useState } from "react";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { CONTACT_DATA, CONTACT_PAGE_DATA } from "@/data/siteData";
import {
  MapPin, Mail, Phone, Clock, Send, CalendarDays,
  Github, Instagram, Linkedin
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const socialIconMap = {
  Github,
  Instagram,
  Linkedin,
};

export default function ContactUs() {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [consultDate, setConsultDate] = useState(undefined);
  const [consultType, setConsultType] = useState("");
  const [sendingInquiry, setSendingInquiry] = useState(false);

  const subjectValue = (formData.subject || "other").trim() || "other";
  const subjectLabel = CONTACT_PAGE_DATA.subjectLabels[subjectValue] || CONTACT_PAGE_DATA.subjectLabels.other;

  const web3formsAccessKey = (process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "").trim();

  const handleInquiry = async (e) => {
    e.preventDefault();
    if (sendingInquiry) return;

    const contactForm = e.currentTarget;
    const btn = contactForm.querySelector("button[type='submit']");
    const originalText = btn?.innerText;

    setSendingInquiry(true);
    if (btn) {
      btn.innerText = "Sending...";
      btn.disabled = true;
    }

    const accessKey = (contactForm.querySelector("input[name='access_key']")?.value || "").trim();
    const accessKeyConfigured = accessKey && accessKey !== "YOUR_ACCESS_KEY_HERE";

    if (!accessKeyConfigured) {
      const name = (contactForm.querySelector("input[name='name']")?.value || "").trim();
      const email = (contactForm.querySelector("input[name='email']")?.value || "").trim();
      const phone = (contactForm.querySelector("input[name='phone']")?.value || "").trim();
      const subjectVal = (contactForm.querySelector("input[name='subject']")?.value || "other").trim();
      const message = (contactForm.querySelector("textarea[name='message']")?.value || "").trim();

      const subject = encodeURIComponent(`New message from Uproot Innovation website${subjectVal ? ` (${subjectVal})` : ""}`);
      const body = encodeURIComponent(
        `Name: ${name}\nEmail: ${email}${phone ? `\nPhone: ${phone}` : ""}${subjectVal ? `\nSubject: ${subjectVal}` : ""}\n\nMessage:\n${message}`
      );

      toast.info("Set Web3Forms access key to enable direct sending. Opening your email app...");
      window.location.href = `mailto:uprootinnovation@gmail.com?subject=${subject}&body=${body}`;

      if (btn) {
        btn.innerText = originalText || "Send Message";
        btn.disabled = false;
      }
      setSendingInquiry(false);
      return;
    }

    try {
      // Build payload manually to control the field order in the email body.
      const payload = new FormData();
      payload.set("access_key", web3formsAccessKey);
      payload.set("from_name", "Uproot Innovation Website");
      payload.set("subject", `New message from Uproot Innovation website (${subjectLabel})`);
      // Fields below appear in this exact order in the Web3Forms email body:
      payload.set("name", formData.name);
      payload.set("category_subject", subjectLabel);
      payload.set("email", formData.email);
      payload.set("phone", formData.phone || "");
      payload.set("message", formData.message);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: payload,
      });

      const data = await response.json();

      if (data?.success) {
        toast.success("Message Sent Successfully!");
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        toast.error(data?.message || "Error sending message. Please try again.");
      }
    } catch {
      toast.error("Error sending message. Please try again.");
    } finally {
      if (btn) {
        btn.innerText = originalText || "Send Message";
        btn.disabled = false;
      }
      setSendingInquiry(false);
    }
  };

  const handleConsultation = (e) => {
    e.preventDefault();
    if (!consultDate) {
      toast.error("Please select a preferred date.");
      return;
    }
    toast.success("Consultation request submitted! Our team will confirm your appointment shortly.");
    setConsultDate(undefined);
    setConsultType("");
  };

  return (
    <div>
      <Toaster position="top-right" />

      <section className="relative py-24 md:py-32 overflow-hidden mesh-bg" data-testid="contact-hero">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#097DC3]/8 text-[#097DC3] text-sm font-semibold uppercase tracking-wider mb-6">
              {CONTACT_PAGE_DATA.hero.tagline}
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#0A0A0A] mb-6">
              {CONTACT_PAGE_DATA.hero.titlePrefix} <span className="text-[#097DC3]">{CONTACT_PAGE_DATA.hero.titleHighlight}</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#475569] max-w-2xl">
              {CONTACT_PAGE_DATA.hero.subtitle}
            </p>
          </motion.div>
        </div>
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-[#8CDDF6]/15 blur-3xl" />
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="inquiry-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            <div className="lg:col-span-3">
              <ScrollReveal>
                <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-4">
                  {CONTACT_PAGE_DATA.inquiry.title}
                </h2>
                <p className="text-[#475569] mb-8">
                  {CONTACT_PAGE_DATA.inquiry.subtitle}
                </p>
                <form onSubmit={handleInquiry} className="space-y-5" data-testid="inquiry-form">
                  <input
                    type="hidden"
                    name="access_key"
                    value={web3formsAccessKey || "YOUR_ACCESS_KEY_HERE"}
                  />
                  <input type="hidden" name="subject" value={subjectLabel} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{CONTACT_PAGE_DATA.inquiry.fields.fullName}</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow"
                        placeholder={CONTACT_PAGE_DATA.inquiry.fields.fullNamePlaceholder}
                        required
                        data-testid="inquiry-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{CONTACT_PAGE_DATA.inquiry.fields.email}</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow"
                        placeholder={CONTACT_PAGE_DATA.inquiry.fields.emailPlaceholder}
                        required
                        data-testid="inquiry-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{CONTACT_PAGE_DATA.inquiry.fields.phone}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow"
                        placeholder={CONTACT_PAGE_DATA.inquiry.fields.phonePlaceholder}
                        data-testid="inquiry-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{CONTACT_PAGE_DATA.inquiry.fields.subject}</label>
                      <Select
                        value={formData.subject}
                        onValueChange={(val) => setFormData({ ...formData, subject: val })}
                      >
                        <SelectTrigger className="w-full px-4 py-3 rounded-xl" data-testid="inquiry-subject">
                          <SelectValue placeholder={CONTACT_PAGE_DATA.inquiry.fields.subjectPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {CONTACT_PAGE_DATA.subjectOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{CONTACT_PAGE_DATA.inquiry.fields.message}</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow resize-none"
                      placeholder={CONTACT_PAGE_DATA.inquiry.fields.messagePlaceholder}
                      required
                      data-testid="inquiry-message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sendingInquiry}
                    className="btn-pill inline-flex items-center gap-2 bg-[#097DC3] text-white px-8 py-3 rounded-full font-semibold text-sm disabled:opacity-60"
                    data-testid="inquiry-submit"
                  >
                    <Send size={16} />
                    {CONTACT_PAGE_DATA.inquiry.submitButton}
                  </button>
                </form>
              </ScrollReveal>
            </div>

            <div className="lg:col-span-2">
              <ScrollReveal delay={0.15}>
                <h2 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-[#0A0A0A] mb-8">
                  {CONTACT_PAGE_DATA.office.title}
                </h2>
                <div className="space-y-6 mb-12">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#097DC3]/8 flex items-center justify-center shrink-0">
                      <MapPin size={18} className="text-[#097DC3]" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#0A0A0A] mb-1">{CONTACT_PAGE_DATA.office.labels.address}</h4>
                      <p className="text-sm text-[#475569]">{CONTACT_DATA.office.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#097DC3]/8 flex items-center justify-center shrink-0">
                      <Mail size={18} className="text-[#097DC3]" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#0A0A0A] mb-1">{CONTACT_PAGE_DATA.office.labels.email}</h4>
                      <p className="text-sm text-[#475569]">{CONTACT_DATA.office.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#097DC3]/8 flex items-center justify-center shrink-0">
                      <Phone size={18} className="text-[#097DC3]" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#0A0A0A] mb-1">{CONTACT_PAGE_DATA.office.labels.phone}</h4>
                      <p className="text-sm text-[#475569]">{CONTACT_DATA.office.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#097DC3]/8 flex items-center justify-center shrink-0">
                      <Clock size={18} className="text-[#097DC3]" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[#0A0A0A] mb-1">{CONTACT_PAGE_DATA.office.labels.workingHours}</h4>
                      <p className="text-sm text-[#475569]">{CONTACT_DATA.office.hours}</p>
                    </div>
                  </div>
                </div>

                <h3 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mb-4">{CONTACT_PAGE_DATA.office.followUs}</h3>
                <div className="flex flex-wrap gap-3">
                  {CONTACT_DATA.social.map((s) => {
                    const mappedName = CONTACT_PAGE_DATA.socialIconMap[s.platform];
                    const Icon = socialIconMap[mappedName];

                    if (!Icon) return null;
                    return (
                      <a
                        key={s.platform}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 rounded-xl bg-[#F8FAFC] border border-slate-100 flex items-center justify-center hover:bg-[#097DC3] hover:text-white hover:border-[#097DC3] transition-colors duration-200 text-[#475569] group"
                        data-testid={`social-${s.platform.toLowerCase()}`}
                      >
                        <Icon size={18} />
                      </a>
                    );
                  })}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
